import { get } from '@ember/object';
import ReportStudentNameFormat from '@linc-technologies/linced-common/enums/reports/report-student-name-format';

type NamePart = 'firstName' | 'lastName';

// ReportName is a student's name as a report shows it.
export interface ReportName {
  firstName: string;
  lastName: string;
  fullName: string;
}

// NamedPerson is the part of a person the report name reads: its name
// computeds, through get(), as they can throw before the fields hydrate.
interface NamedPerson {
  get(key: string): unknown;
}

const NAME_PARTS: Readonly<
  Record<
    NamePart,
    { formatKey: string; legacyKey: string; legalKey: string; legal: string }
  >
> = {
  firstName: {
    formatKey: 'studentFirstNameFormat',
    legacyKey: 'firstNameKey',
    legalKey: 'core:firstNameLegal',
    legal: 'legalFirstName',
  },
  lastName: {
    formatKey: 'studentLastNameFormat',
    legacyKey: 'lastNameKey',
    legalKey: 'core:lastNameLegal',
    legal: 'legalLastName',
  },
};

/**
 * Which of the student's names (ReportStudentNameFormat PREFERRED or LEGAL)
 * a report from this template shows for part ('firstName' or 'lastName').
 *
 * Templates saved before the formats moved onto the template (Unspecified)
 * use that name's key on the template's student-name-avatar component,
 * passed as legacyHeader: LEGAL for the legal key, otherwise PREFERRED.
 *
 * @param {Object} template the report template (or anything with the name formats)
 * @param {Object} [legacyHeader] the template's student-name-avatar component
 * @param {string} part 'firstName' or 'lastName'
 * @return {number} ReportStudentNameFormat.PREFERRED or LEGAL
 */
export function resolveNameFormat(
  template: object | null | undefined,
  legacyHeader: object | null | undefined,
  part: NamePart,
): number {
  const { formatKey, legacyKey, legalKey } = NAME_PARTS[part];
  const format = template ? get(template, formatKey) : null;
  if (
    format === ReportStudentNameFormat.LEGAL ||
    format === ReportStudentNameFormat.PREFERRED
  ) {
    return format;
  }
  const key = legacyHeader && get(legacyHeader, legacyKey);
  return key === legalKey
    ? ReportStudentNameFormat.LEGAL
    : ReportStudentNameFormat.PREFERRED;
}

// Reads one of the person's name computeds. They read person fields, and if
// the fields haven't hydrated yet the read can throw the "special kind of
// Ember object (a proxy)" assertion rather than return undefined, so treat
// that as no name yet.
function readName(person: NamedPerson, key: string): string {
  try {
    return (person.get(key) as string | undefined) || '';
  } catch {
    return '';
  }
}

/**
 * The student's name as a report shows it, each of first and last name in
 * the format resolveNameFormat picks for it. Preferred falls back to the
 * legal name when there is no preferred value (person.firstName / lastName),
 * and legal falls back to the preferred name.
 *
 * Mirrors studentNameParts in the reports service's publish snapshot.
 *
 * @param {Object} person the learner
 * @param {Object} template the report template (or anything with the name formats)
 * @param {Object} [legacyHeader] the template's student-name-avatar component
 * @return {{firstName: string, lastName: string, fullName: string}}
 */
export default function reportStudentName(
  person: NamedPerson | null | undefined,
  template?: object | null,
  legacyHeader?: object | null,
): ReportName {
  if (!person) return { firstName: '', lastName: '', fullName: '' };

  const name: Record<NamePart, string> = { firstName: '', lastName: '' };
  (Object.keys(NAME_PARTS) as NamePart[]).forEach((part) => {
    const preferred = readName(person, part);
    name[part] =
      resolveNameFormat(template, legacyHeader, part) ===
      ReportStudentNameFormat.LEGAL
        ? readName(person, NAME_PARTS[part].legal) || preferred
        : preferred;
  });

  return {
    firstName: name.firstName,
    lastName: name.lastName,
    fullName: `${name.firstName} ${name.lastName}`.trim(),
  };
}
